import * as d3 from 'd3-time';
import { DateRange, DateTrunc, FiscalYearStartMonth } from '../models';
import { Formatters } from '../../gen-src/Localize';
import { Messages } from '../localization';
import * as moment from 'moment';

type DateFormatFunc = (a: Date) => string;

export interface DateFormatterCollection {
  readonly dateTruncContainsTime: () => boolean;
  readonly comparisonWidget: (firstDate: Date, lastDate: Date, multiLine?: boolean) => string;
  readonly legend: (range: DateRange, multiLine?: boolean) => string;
  readonly adjustAndCompare: (range: DateRange) => string;
  readonly tooltip: (a: Date, multiLine?: boolean) => string;
  readonly banTimestamp: DateFormatFunc;
  readonly axisTick: DateFormatFunc;
  readonly dateRangeSlashFormat: (startDate: Date, endDate: Date) => string;
  readonly slashFormat: (date: Date) => string;
  readonly configurationInfoStartDate: (date: Date) => string;
}

export const dateFormatterFactory = (
  dateTrunc: DateTrunc,
  startMonth: FiscalYearStartMonth = FiscalYearStartMonth.JANUARY
): DateFormatterCollection => ({
  dateTruncContainsTime(): boolean {
    switch (dateTrunc) {
      case DateTrunc.HOUR:
      case DateTrunc.MINUTE:
      case DateTrunc.SECOND:
      case DateTrunc.UNKNOWN:
        return true;
      default:
        return false;
    }
  },

  comparisonWidget(firstDate: Date, lastDate: Date, multiLine?: boolean): string {
    const [format1, format2] = formatsForPair(firstDate, lastDate, dateTrunc, startMonth);

    const first = format1(firstDate);
    const last = format2(lastDate);

    if (multiLine) {
      return Messages.metricsComparisonTimestampTwoLines({ first, last });
    } else {
      return Messages.metricsComparisonTimestamp({ first, last });
    }
  },

  legend(range: DateRange, multiLine?: boolean): string {
    const [format1, format2] = formatsForPair(range.first, range.last, dateTrunc, startMonth);
    const firstLine = `${format1(range.first)} —`;
    const secondLine = `${format2(range.last)}`;
    const singleLine = `${firstLine} ${secondLine}`;

    if (multiLine) {
      return firstLine + '\n' + secondLine;
    } else {
      return singleLine;
    }
  },

  adjustAndCompare(range: DateRange): string {
    const [format1, format2] = formatsForPair(range.first, range.last, dateTrunc, startMonth);
    return `${format1(range.first)} — ${format2(range.last)}`;
  },

  tooltip(date: Date, multiLine?: boolean): string {
    return tooltipFormatterForTrunc(dateTrunc, startMonth, multiLine)(date);
  },

  banTimestamp(date: Date): string {
    return banTimestampFormatterForTrunc(dateTrunc, startMonth)(date);
  },

  axisTick(date: Date): string {
    if (fiscalYearStartsInJanuary(startMonth)) {
      return tickFormat(date);
    }

    switch (dateTrunc) {
      case DateTrunc.YEAR:
        return fiscalYearAwareYearFormatter(startMonth)(date);
      case DateTrunc.QUARTER:
        return fiscalYearAwareQuarterFormatter(startMonth)(date);
      default:
        return tickFormat(date);
    }
  },

  dateRangeSlashFormat(startDate: Date, endDate: Date): string {
    const startSlashDate = Formatters.slashDate(startDate);
    const endSlashDate = Formatters.slashDate(endDate);
    return Messages.metricsDateRangeSlashFormat({ startDate: startSlashDate, endDate: endSlashDate });
  },

  slashFormat(date: Date): string {
    return Formatters.slashDate(date);
  },

  configurationInfoStartDate(date: Date): string {
    const formattedDate = Formatters.slashDate(date);
    return Messages.configurationInfoCustomStartDate({ date: formattedDate });
  },
});

const formatsForPair = (
  date1: Date,
  date2: Date,
  dateTrunc: DateTrunc,
  startMonth: FiscalYearStartMonth
): readonly [DateFormatFunc, DateFormatFunc] => {
  switch (dateTrunc) {
    case DateTrunc.SECOND: {
      const first = yearsAreDifferent(date1, date2)
        ? (a: Date) => timeThenDate(a, Formatters.timeWithSeconds, Formatters.wordyDate)
        : datesAreDifferent(date1, date2)
        ? (a: Date) => timeThenDate(a, Formatters.timeWithSeconds, Formatters.monthAndDay)
        : Formatters.timeWithSeconds;
      const second = (a: Date) => timeThenDate(a, Formatters.timeWithSeconds, Formatters.wordyDate);
      return [first, second];
    }
    case DateTrunc.MINUTE: {
      const first = yearsAreDifferent(date1, date2)
        ? (a: Date) => timeThenDate(a, Formatters.timeToMinute, Formatters.wordyDate)
        : datesAreDifferent(date1, date2)
        ? (a: Date) => timeThenDate(a, Formatters.timeToMinute, Formatters.monthAndDay)
        : Formatters.timeToMinute;
      const second = (a: Date) => timeThenDate(a, Formatters.timeToMinute, Formatters.wordyDate);
      return [first, second];
    }
    case DateTrunc.HOUR: {
      const first = yearsAreDifferent(date1, date2)
        ? (a: Date) => timeThenDate(a, Formatters.timeToHour, Formatters.wordyDate)
        : datesAreDifferent(date1, date2)
        ? (a: Date) => timeThenDate(a, Formatters.timeToHour, Formatters.monthAndDay)
        : Formatters.timeToHour;
      const second = (a: Date) => timeThenDate(a, Formatters.timeToHour, Formatters.wordyDate);
      return [first, second];
    }
    case DateTrunc.ISO_WEEKDAY:
    case DateTrunc.DAY:
    case DateTrunc.ISO_WEEK:
    case DateTrunc.WEEK: {
      const first = yearsAreDifferent(date1, date2) ? Formatters.wordyDate : Formatters.monthAndDay;
      const second = Formatters.wordyDate;
      return [first, second];
    }
    case DateTrunc.MONTH: {
      const first = yearsAreDifferent(date1, date2) ? Formatters.wordyMonthYear : Formatters.month;
      const second = Formatters.wordyMonthYear;
      return [first, second];
    }
    case DateTrunc.ISO_QTR:
    case DateTrunc.QUARTER: {
      if (fiscalYearStartsInJanuary(startMonth)) {
        const first = yearsAreDifferent(date1, date2) ? Formatters.quarterYear : quarterNoYear;
        const second = Formatters.quarterYear;
        return [first, second];
      } else {
        const first = fiscalYearAwareQuarterFormatter(startMonth);
        const second = yearsAreDifferent(date1, date2, startMonth)
          ? fiscalYearAwareQuarterFormatter(startMonth)
          : fiscalYearAwareQuarterNoYearFormatter(startMonth);
        return [first, second];
      }
    }
    case DateTrunc.ISO_YEAR:
    case DateTrunc.YEAR: {
      return [fiscalYearAwareYearFormatter(startMonth), fiscalYearAwareYearFormatter(startMonth)];
    }
    case DateTrunc.NONE:
    case DateTrunc.UNKNOWN:
    case undefined:
    default: {
      return [
        (a: Date) => timeThenDate(a, Formatters.timeToMinute, Formatters.wordyDate),
        (a: Date) => timeThenDate(a, Formatters.timeToMinute, Formatters.wordyDate),
      ];
    }
  }
};

const yearsAreDifferent = (date1: Date, date2: Date, fiscalYearStartMonth: FiscalYearStartMonth = 1) => {
  if (fiscalYearStartsInJanuary(fiscalYearStartMonth)) {
    return date1.getFullYear() !== date2.getFullYear();
  } else {
    return fyForDate(date1, fiscalYearStartMonth) !== fyForDate(date2, fiscalYearStartMonth);
  }
};

const fyForDate = (date: Date, startMonth: FiscalYearStartMonth) => {
  if (beforeFYStart(date, startMonth)) {
    return date.getFullYear();
  } else {
    return date.getFullYear() + 1;
  }
};

const datesAreDifferent = (date1: Date, date2: Date) =>
  date1.getDate() !== date2.getDate() || date1.getMonth() !== date2.getMonth();

export const banTimestampFormatterForTrunc = (dateTrunc: DateTrunc, fiscalYearStartMonth: number): DateFormatFunc => {
  switch (dateTrunc) {
    case DateTrunc.SECOND: {
      return Formatters.wordyDateWithTimeToSecond;
    }
    case DateTrunc.MINUTE: {
      return Formatters.wordyDateWithTimeToMinute;
    }
    case DateTrunc.HOUR: {
      return Formatters.wordyDateWithTimeToHour;
    }
    case DateTrunc.ISO_WEEKDAY:
    case DateTrunc.DAY: {
      return Formatters.wordyDate;
    }
    case DateTrunc.MONTH: {
      return Formatters.wordyMonthYear;
    }
    case DateTrunc.ISO_WEEK:
    case DateTrunc.WEEK: {
      return (a) => Messages.metricsWeekOf({ week: Formatters.wordyDate(a) });
    }
    case DateTrunc.ISO_QTR:
    case DateTrunc.QUARTER: {
      return fiscalYearAwareQuarterFormatter(fiscalYearStartMonth);
    }
    case DateTrunc.ISO_YEAR:
    case DateTrunc.YEAR: {
      return fiscalYearAwareYearFormatter(fiscalYearStartMonth);
    }
    case DateTrunc.NONE:
    case DateTrunc.UNKNOWN:
    case undefined:
    default: {
      return Formatters.wordyDateWithTimeToMinute;
    }
  }
};

export const tooltipFormatterForTrunc = (
  dateTrunc: DateTrunc,
  startMonth: FiscalYearStartMonth,
  multiLine?: boolean
): DateFormatFunc => {
  switch (dateTrunc) {
    case DateTrunc.SECOND: {
      return Formatters.timeWithSeconds;
    }
    case DateTrunc.MINUTE: {
      return multilineMinuteFormatter(multiLine);
    }
    case DateTrunc.HOUR: {
      return multiLine
        ? (date: Date) => {
            return timeBelowDate(date, Formatters.slashDate, Formatters.timeToHour);
          }
        : Formatters.dateWithTimeToHour;
    }
    case DateTrunc.ISO_WEEKDAY:
    case DateTrunc.DAY: {
      return Formatters.slashDate;
    }
    case DateTrunc.MONTH: {
      return Formatters.slashMonthYear;
    }
    case DateTrunc.ISO_WEEK:
    case DateTrunc.WEEK: {
      return multiLine
        ? (a) => Messages.metricsWeekOfTwoLines({ week: Formatters.slashDate(a) })
        : (a) => Messages.metricsWeekOf({ week: Formatters.slashDate(a) });
    }
    case DateTrunc.ISO_QTR:
    case DateTrunc.QUARTER: {
      return fiscalYearAwareQuarterFormatter(startMonth);
    }
    case DateTrunc.ISO_YEAR:
    case DateTrunc.YEAR: {
      return fiscalYearAwareYearFormatter(startMonth);
    }
    case DateTrunc.NONE:
    case DateTrunc.UNKNOWN:
    case undefined:
    default: {
      return multilineMinuteFormatter(multiLine);
    }
  }
};

const fiscalYearStartsInJanuary = (startMonth: FiscalYearStartMonth) => startMonth === FiscalYearStartMonth.JANUARY;

const beforeFYStart = (date: Date, startMonth: number) => date.getMonth() + 1 < startMonth;

export const fiscalYearAwareQuarterFormatter = (startMonth: FiscalYearStartMonth) => {
  return (date: Date) => {
    if (fiscalYearStartsInJanuary(startMonth)) {
      return Formatters.quarterYear(date);
    } else {
      const fy = fiscalYearAwareYearFormatter(startMonth)(date);
      const qN = fiscalYearAwareQuarterNoYearFormatter(startMonth)(date);
      return Messages.metricsFYQuarter({ fy, qN });
    }
  };
};

export const fiscalYearAwareQuarterNoYearFormatter = (startMonth: FiscalYearStartMonth) => {
  return (date: Date) => {
    const offset = monthOffset(startMonth);
    const newDate = new Date(date.valueOf());
    newDate.setMonth(date.getMonth() + offset);
    return quarterNoYear(newDate);
  };
};

const monthOffset = (startMonth: FiscalYearStartMonth): number => {
  const o = (startMonth - 13) * -1;
  if (o === 12) {
    return 0;
  }

  return o;
};

const fiscalYearAwareYearFormatter = (startMonth: number) => {
  return (date: Date) => {
    if (fiscalYearStartsInJanuary(startMonth)) {
      return Formatters.year(date);
    } else {
      if (beforeFYStart(date, startMonth)) {
        return Messages.metricsFY({ year: Formatters.year(date) });
      } else {
        const newDate = new Date(date.valueOf());
        newDate.setFullYear(date.getFullYear() + 1);
        return Messages.metricsFY({ year: Formatters.year(newDate) });
      }
    }
  };
};

function multilineMinuteFormatter(multiLine: boolean | undefined): DateFormatFunc {
  return multiLine
    ? (date: Date) => {
        return timeBelowDate(date, Formatters.slashDate, Formatters.timeToMinute);
      }
    : Formatters.dateWithTimeToMinute;
}

export function tickFormat(date: Date): string {
  return (
    d3.timeMinute(date) < date
      ? Formatters.timeWithSeconds
      : d3.timeHour(date) < date
      ? Formatters.timeToMinute
      : d3.timeDay(date) < date
      ? Formatters.timeToMinute
      : d3.timeMonth(date) < date
      ? Formatters.monthAndDay
      : d3.timeYear(date) < date
      ? Formatters.month
      : Formatters.year
  )(date);
}

export const lastRefreshedAbsoluteFormatter = (date: Date): string => {
  const day = Formatters.wordyDate(date);
  const time = Formatters.timeWithMinutes(date);
  return Messages.metricsSyncedAtTime({ day, time });
};

// The Globalize library doesn't seem to like the 'QQQ' skeleton, so we just extract the quarter from the 'yQQQ'.
const quarterNoYear = (a: Date) => {
  const quarter = Formatters.quarterYear(a);
  const year = Formatters.year(a);
  return quarter.replace(year, '').trim();
};

// There isn't a way to specify time before date in a single skeleton in the Globalize library.
// So we just put them together with a string formatter.
const timeThenDate = (a: Date, timeFormatter: DateFormatFunc, dateFormatter: DateFormatFunc) => {
  return `${timeFormatter(a)}, ${dateFormatter(a)}`;
};

const timeBelowDate = (a: Date, dateFormatter: DateFormatFunc, timeFormatter: DateFormatFunc) => {
  return `${dateFormatter(a)}\n${timeFormatter(a)}`;
};

// Used for Date objects in UTC which return ISO strings like "2021-08-20T18:09:19.443Z",
// "2021-08-20T00:00:00Z", etc. We want a zoneless ISO string "2021-08-20T00:00:00" instead.
export const dateISOStringWithoutTimezone = (date: Date): string => {
  if (isNaN(date.getTime())) {
    return date.toDateString();
  }
  // We only care about years, months, and days here
  return moment(date).format('YYYY-MM-DDT00:00:00');
};

export const abbreviatedRelativeDateWindow = (dateTrunc: DateTrunc, n: number) => {
  switch (dateTrunc) {
    case DateTrunc.SECOND:
      return n === 1 ? Messages.secondsWindowSingular() : Messages.secondsWindowPlural({ number: n.toString() });
    case DateTrunc.MINUTE:
      return n === 1 ? Messages.minutesWindowSingular() : Messages.minutesWindowPlural({ number: n.toString() });
    case DateTrunc.HOUR:
      return n === 1 ? Messages.hoursWindowSingular() : Messages.hoursWindowPlural({ number: n.toString() });
    case DateTrunc.DAY:
    case DateTrunc.ISO_WEEKDAY:
      return n === 1 ? Messages.daysWindowSingular() : Messages.daysWindowPlural({ number: n.toString() });
    case DateTrunc.WEEK:
    case DateTrunc.ISO_WEEK:
      return n === 1 ? Messages.weeksWindowSingular() : Messages.weeksWindowPlural({ number: n.toString() });
    case DateTrunc.MONTH:
      return n === 1 ? Messages.monthsWindowSingular() : Messages.monthsWindowPlural({ number: n.toString() });
    case DateTrunc.QUARTER:
    case DateTrunc.ISO_QTR:
      return n === 1 ? Messages.quartersWindowSingular() : Messages.quartersWindowPlural({ number: n.toString() });
    case DateTrunc.YEAR:
    case DateTrunc.ISO_YEAR:
      return n === 1 ? Messages.yearsWindowSingular() : Messages.yearsWindowPlural({ number: n.toString() });
    case DateTrunc.UNKNOWN:
    default:
      return n === 1 ? Messages.marksWindowSingular() : Messages.marksWindowPlural({ number: n.toString() });
  }
};

export const windowPickerStartDate = (date: Date): string => {
  const slashDate = Formatters.slashDate(date);
  return Messages.windowPickerDefinedStart({ date: slashDate });
};
